/**
 * Conversation pane for the plugin builder.
 * Displays chat messages, streaming text, verbose events, and guided flow panels.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  Wrench,
  Cpu,
  AlertTriangle,
  Terminal,
  List,
} from 'lucide-react';
import type { VerboseEvent } from '../types';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface PluginConversationPaneProps {
  messages: Message[];
  isProcessing: boolean;
  streamingText: string;
  emptyStateContent?: React.ReactNode;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  // Verbose events
  verboseHistory?: Record<string, VerboseEvent[]>;
  pendingVerboseEvents?: VerboseEvent[];
  isAdmin?: boolean;
  // Suggested next steps
  suggestedNextSteps?: string[];
  onSuggestionClick?: (suggestion: string) => void;
  className?: string;
}

function MessageBubble({ message, isStreaming }: { message: Message; isStreaming?: boolean }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-4 py-3',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
        )}
      >
        <p className="whitespace-pre-wrap text-sm">
          {message.content}
          {isStreaming && <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse" />}
        </p>
      </div>
    </div>
  );
}

/** Parse tool results to extract warnings */
function parseToolResultWarnings(result: unknown): string[] {
  if (typeof result !== 'string') return [];
  const warnings: string[] = [];
  const warningPatterns = [
    /WARNING: You have made (\d+) edits/,
    /WARNING: You have made (\d+) changes without/,
    /unknown prop/i,
    /missing required prop/i,
  ];
  for (const pattern of warningPatterns) {
    const match = result.match(pattern);
    if (match) warnings.push(match[0]);
  }
  return warnings;
}

const EVENT_ICONS: Record<VerboseEvent['type'], React.ElementType> = {
  system_prompt: Terminal,
  tool_call: Wrench,
  tool_result: Wrench,
  model_info: Cpu,
  tools_available: List,
};

function VerboseEventItem({ event }: { event: VerboseEvent }) {
  const [expanded, setExpanded] = useState(event.data.isError === true);
  const Icon = EVENT_ICONS[event.type] ?? Terminal;
  const isError = event.data.isError === true;
  const warnings =
    event.type === 'tool_result' ? parseToolResultWarnings(event.data.toolResult) : [];

  return (
    <div
      className={cn(
        'text-xs border-l-2 pl-2 ml-2',
        isError ? 'border-red-400' : warnings.length > 0 ? 'border-yellow-400' : 'border-muted',
      )}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground w-full text-left"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Icon className="h-3 w-3" />
        <span className="font-mono">
          {event.type === 'system_prompt' && 'System Prompt'}
          {event.type === 'model_info' && `Model: ${event.data.model} (${event.data.intent})`}
          {event.type === 'tools_available' && `${event.data.tools?.length ?? 0} tools available`}
          {event.type === 'tool_call' && `Call: ${event.data.toolName}`}
          {event.type === 'tool_result' && (
            <>
              Result: {event.data.toolName}
              {isError && <AlertTriangle className="h-3 w-3 inline ml-1 text-red-500" />}
              {warnings.length > 0 && !isError && (
                <AlertTriangle className="h-3 w-3 inline ml-1 text-yellow-500" />
              )}
            </>
          )}
        </span>
      </button>

      {expanded && (
        <pre className="mt-1 p-2 rounded bg-muted/50 text-[11px] font-mono overflow-x-auto max-h-[200px] overflow-y-auto">
          {event.type === 'system_prompt' && (event.data.prompt?.slice(0, 500) ?? '')}
          {event.type === 'tool_call' && JSON.stringify(event.data.toolInput, null, 2)}
          {event.type === 'tool_result' &&
            (typeof event.data.toolResult === 'string'
              ? event.data.toolResult
              : JSON.stringify(event.data.toolResult, null, 2))}
          {event.type === 'tools_available' &&
            event.data.tools?.map((t) => `${t.name}: ${t.description}`).join('\n')}
          {event.type === 'model_info' &&
            `Model: ${event.data.model}\nIntent: ${event.data.intent}`}
        </pre>
      )}

      {warnings.length > 0 && !expanded && (
        <div className="mt-0.5">
          {warnings.map((w, i) => (
            <p key={i} className="text-[10px] text-yellow-600 dark:text-yellow-400">
              {w}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function VerboseEventsBlock({ events }: { events: VerboseEvent[] }) {
  const [expanded, setExpanded] = useState(false);
  const hasErrors = events.some((e) => e.data.isError);

  if (events.length === 0) return null;

  return (
    <div className="my-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground',
          hasErrors && 'text-red-500',
        )}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Terminal className="h-3 w-3" />
        <span>{events.length} verbose events</span>
        {hasErrors && <AlertTriangle className="h-3 w-3 text-red-500" />}
      </button>

      {expanded && (
        <div className="mt-1 space-y-1">
          {events.map((event, i) => (
            <VerboseEventItem key={i} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PluginConversationPane({
  messages,
  isProcessing,
  streamingText,
  emptyStateContent,
  headerContent,
  footerContent,
  verboseHistory,
  pendingVerboseEvents,
  isAdmin = false,
  suggestedNextSteps,
  onSuggestionClick,
  className,
}: PluginConversationPaneProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingText, scrollToBottom]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {headerContent}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          emptyStateContent ? (
            emptyStateContent
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-muted-foreground">
                Describe what you want to build below.
              </p>
            </div>
          )
        ) : (
          messages.map((message, index) => {
            // Show verbose events after each assistant message (if admin)
            const messageKey = message.timestamp || String(index);
            const events = isAdmin && verboseHistory ? verboseHistory[messageKey] || [] : [];

            return (
              <div key={index}>
                <MessageBubble message={message} />
                {events.length > 0 && <VerboseEventsBlock events={events} />}
              </div>
            );
          })
        )}

        {/* Streaming message */}
        {isProcessing &&
          (streamingText ? (
            <div>
              <MessageBubble message={{ role: 'assistant', content: streamingText }} isStreaming />
              {/* Show pending verbose events during streaming */}
              {isAdmin && pendingVerboseEvents && pendingVerboseEvents.length > 0 && (
                <VerboseEventsBlock events={pendingVerboseEvents} />
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Fias AI is thinking...</span>
            </div>
          ))}

        {/* Suggested next steps */}
        {!isProcessing &&
          suggestedNextSteps &&
          suggestedNextSteps.length > 0 &&
          onSuggestionClick && (
            <div className="flex flex-wrap gap-2 pt-2">
              {suggestedNextSteps.map((step, i) => (
                <button
                  key={i}
                  onClick={() => onSuggestionClick(step)}
                  className="px-3 py-1.5 text-xs rounded-full border bg-background hover:bg-muted transition-colors"
                >
                  {step}
                </button>
              ))}
            </div>
          )}
      </div>
      {footerContent}
    </div>
  );
}
